import { useState, useEffect, useCallback } from 'react'
import type { CutleryType } from './CutlerySVGs'
import type { Difficulty } from './useCutlery'
import type { CoverOrder } from './tableData'
import { generateCovers } from './tableData'
import { shuffle } from '../utils'

export type { CoverOrder }

export interface TableCutleryItem {
  id:   string
  type: CutleryType
}

export interface CoverState {
  order:    CoverOrder
  placed:   CutleryType[]   // types already placed on this cover
  complete: boolean
}

export interface TableState {
  covers:       CoverState[]
  pile:         TableCutleryItem[]
  selectedId:   string | null
  wrongCoverId: string | null
  openMenuId:   string | null
  moves:        number
  startTime:    number | null
  endTime:      number | null
  gameComplete: boolean
}

const EXTRA_DISTRACTORS: Record<Difficulty, number> = {
  easy:   2,
  medium: 3,
  hard:   4,
}

function initTableState(difficulty: Difficulty): TableState {
  const orders = generateCovers(difficulty)

  // Exact pieces needed
  const required: TableCutleryItem[] = orders.flatMap((order) =>
    order.required.map((type, i) => ({ id: `pile-${order.id}-${type}-${i}`, type }))
  )

  // Distractor pieces — only types that are actually needed so rejections feel fair
  const activeTypes = [...new Set(orders.flatMap((o) => o.required))] as CutleryType[]
  const distractors: TableCutleryItem[] = Array.from({ length: EXTRA_DISTRACTORS[difficulty] }, (_, i) => ({
    id:   `extra-${i}`,
    type: activeTypes[Math.floor(Math.random() * activeTypes.length)],
  }))

  const pile = shuffle([...required, ...distractors])

  return {
    covers:       orders.map((order) => ({ order, placed: [], complete: false })),
    pile,
    selectedId:   null,
    wrongCoverId: null,
    openMenuId:   null,
    moves:        0,
    startTime:    null,
    endTime:      null,
    gameComplete: false,
  }
}

function applyPlacement(prev: TableState, itemId: string, coverId: string): TableState {
  if (prev.wrongCoverId) return prev

  const item       = prev.pile.find((p) => p.id === itemId)
  const coverIndex = prev.covers.findIndex((c) => c.order.id === coverId)
  if (!item || coverIndex === -1) return prev

  const cover = prev.covers[coverIndex]
  if (cover.complete) return prev

  const newMoves  = prev.moves + 1
  const startTime = prev.startTime ?? Date.now()

  // Accept only if this type is required and not yet placed on this cover
  const needed = cover.order.required.includes(item.type) && !cover.placed.includes(item.type)

  if (needed) {
    const newPile    = prev.pile.filter((p) => p.id !== itemId)
    const newPlaced  = [...cover.placed, item.type]
    const coverDone  = newPlaced.length === cover.order.required.length
    const newCovers  = prev.covers.map((c, i) =>
      i === coverIndex ? { ...c, placed: newPlaced, complete: coverDone } : c
    )
    const gameComplete = newCovers.every((c) => c.complete)
    return {
      ...prev,
      pile:         newPile,
      covers:       newCovers,
      selectedId:   null,
      moves:        newMoves,
      startTime,
      gameComplete,
      endTime:      gameComplete ? Date.now() : prev.endTime,
    }
  }

  return { ...prev, selectedId: null, wrongCoverId: coverId, moves: newMoves, startTime }
}

export function useTableSetting(difficulty: Difficulty) {
  const [state, setState] = useState<TableState>(() => initTableState(difficulty))

  const resetGame = useCallback(() => {
    setState(initTableState(difficulty))
  }, [difficulty])

  // Clear wrong-cover shake after animation
  useEffect(() => {
    if (!state.wrongCoverId) return
    const timer = setTimeout(() => {
      setState((prev) => ({ ...prev, wrongCoverId: null }))
    }, 650)
    return () => clearTimeout(timer)
  }, [state.wrongCoverId])

  const openMenu = useCallback((coverId: string) => {
    setState((prev) => ({
      ...prev,
      openMenuId: prev.openMenuId === coverId ? null : coverId,
    }))
  }, [])

  const closeMenu = useCallback(() => {
    setState((prev) => ({ ...prev, openMenuId: null }))
  }, [])

  const selectItem = useCallback((id: string) => {
    setState((prev) => {
      if (prev.wrongCoverId) return prev
      if (prev.selectedId === id) return { ...prev, selectedId: null }
      return { ...prev, selectedId: id, startTime: prev.startTime ?? Date.now() }
    })
  }, [])

  const placeInCover = useCallback((coverId: string) => {
    setState((prev) => {
      if (!prev.selectedId) return prev
      return applyPlacement(prev, prev.selectedId, coverId)
    })
  }, [])

  const placeItemById = useCallback((itemId: string, coverId: string) => {
    setState((prev) => applyPlacement(prev, itemId, coverId))
  }, [])

  return { state, resetGame, selectItem, placeInCover, placeItemById, openMenu, closeMenu }
}
